import { Identifiable } from '@wilson/base';
import {
  BookingSource,
  ProductBooking,
  ProductType,
  ProductUnit,
} from '@wilson/interfaces';
import {
  CalendarDateLike,
  isCalendarDateLike,
  isUUIDLike,
  toCalendarDate,
} from '@wilson/utils';

import { MIN_QUANTITY } from './product-booking-quantity.constants';

export interface BookedProduct {
  readonly id: string;
  readonly name: string;
  readonly shortName: string | null;
  readonly unit: ProductUnit | null;
  readonly pricePerUnit: number | null;
  readonly operativeSelectable: boolean;
  readonly type: ProductType;
}

export class ProductBookingModel extends Identifiable {
  readonly productId: string;
  readonly agreementId: string;
  readonly date: CalendarDateLike;
  readonly quantity: number;
  readonly organizationalUnitId: string;
  readonly source: BookingSource;
  readonly projectId?: string | null;
  readonly createdById?: string | null;
  readonly product: BookedProduct;

  constructor(data: ProductBookingModel) {
    super(data);
    ProductBookingModel.validate(data);

    this.productId = data.productId;
    this.agreementId = data.agreementId;
    this.date = data.date;
    this.quantity = data.quantity;
    this.organizationalUnitId = data.organizationalUnitId;
    this.source = data.source;
    this.product = data.product;
    this.projectId = data.projectId;
    this.createdById = data.createdById;
  }

  isEditableBy(userId: string | null): boolean {
    return (
      userId !== null &&
      this.createdById === userId &&
      ProductBookingModel.isOperativeBookable(this.product)
    );
  }

  static isOperativeBookable(product: {
    operativeSelectable?: boolean;
    type?: ProductType;
  }): boolean {
    return (
      product.operativeSelectable === true &&
      product.type === ProductType.Booked
    );
  }

  static fromApiResponse(booking: ProductBooking): ProductBookingModel {
    return new ProductBookingModel({
      ...booking,
      date: toCalendarDate(booking.date),
      quantity: Number(booking.quantity),
      product: {
        ...booking.product,
        pricePerUnit:
          booking.product.pricePerUnit == null
            ? null
            : Number(booking.product.pricePerUnit),
      },
    } as ProductBookingModel);
  }

  static validate(data: ProductBookingModel): void {
    const errors = new Set<string>();

    if (!isUUIDLike(data.productId)) {
      errors.add('productId must be a valid UUID');
    }

    if (!isUUIDLike(data.agreementId)) {
      errors.add('agreementId must be a valid UUID');
    }

    if (!isUUIDLike(data.organizationalUnitId)) {
      errors.add('organizationalUnitId must be a valid UUID');
    }

    if (!isCalendarDateLike(data.date)) {
      errors.add(`date must be a yyyy-MM-dd string (received: ${data.date})`);
    }

    if (!Number.isFinite(data.quantity) || data.quantity < MIN_QUANTITY) {
      errors.add(
        `quantity must be a number of at least ${MIN_QUANTITY} (received: ${data.quantity})`,
      );
    }

    if (!Object.values(BookingSource).includes(data.source)) {
      errors.add(`source must be a BookingSource (received: ${data.source})`);
    }

    if (errors.size > 0) {
      throw new Error(
        `ProductBookingModel validation failed: ${Array.from(errors).join(
          ', ',
        )}`,
      );
    }
  }
}
